import { describeIntakeError } from './intakeLinks';
import { formatWait, retryAfterSeconds } from './apiErrors';
import { siteOrigin } from '../platform';

/**
 * Permanent public forms in words and rules (KPR-513; keepr-api docs/SCHEMA.md
 * § Public forms). The API judges every rule here again — these exist so a
 * manager hears "no" before pressing Save, never instead of the server.
 */

// keepr-api utils/publicAddresses.js SLUG_MIN / SLUG_MAX / SLUG_RE. Only the
// SHAPE is mirrored, and the one rule a person cannot guess ("anything with
// keepr in it"). The reserved words are the server's to say (400
// slug_reserved, with its own sentence): a copy of that list here would drift
// the first time the API widened it, and a word missing from the copy only
// moves the refusal from before Save to after it.
export const SLUG_MIN = 3;
export const SLUG_MAX = 48;
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** Why `slug` cannot be an address, in words, or null when it may be. */
export function slugProblem(slug, what = 'An address') {
	const raw = typeof slug === 'string' ? slug.trim() : '';
	if (!raw) return `${what} is needed.`;
	if (raw.length < SLUG_MIN || raw.length > SLUG_MAX || !SLUG_RE.test(raw)) {
		return `${what} is ${SLUG_MIN} to ${SLUG_MAX} lower-case letters, digits and hyphens, and does not start or end with a hyphen.`;
	}
	if (raw.includes('keepr')) return `${what} cannot contain “keepr”.`;
	return null;
}

/**
 * A slug suggested from a name ("Report a pothole" → "report-a-pothole"), or
 * '' when the name makes none — the API's `suggestSlug`, so what a manager is
 * offered here is what the server would have suggested.
 */
export function suggestSlug(name) {
	const base = `${name || ''}`.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase()
		.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, SLUG_MAX).replace(/-+$/g, '');
	return slugProblem(base) ? '' : base;
}

// keepr-api utils/publicForms.js LIMIT_BOUNDS: [min, max, default].
export const LIMIT_BOUNDS = Object.freeze({
	perDay: Object.freeze([1, 2000, 100]),
	perWeek: Object.freeze([1, 10000, 500]),
	maxPending: Object.freeze([1, 1000, 200]),
});
export const defaultLimits = () => ({
	perDay: LIMIT_BOUNDS.perDay[2],
	perWeek: LIMIT_BOUNDS.perWeek[2],
	maxPending: LIMIT_BOUNDS.maxPending[2],
});
export const MAX_FORM_NAME = 100;

/** Why these limits would be refused, in words, or null. */
export function limitsProblem(limits) {
	const words = { perDay: 'A day', perWeek: 'A week', maxPending: 'Waiting for review' };
	for (const [key, [min, max]] of Object.entries(LIMIT_BOUNDS)) {
		const value = limits?.[key];
		if (!Number.isInteger(value) || value < min || value > max) return `${words[key]} must be a whole number from ${min} to ${max.toLocaleString('en-US')}.`;
	}
	if (limits.perWeek < limits.perDay) return 'A week must take at least as many submissions as a day.';
	return null;
}

/**
 * The address a person types, scans or prints: this web app's origin and the
 * address's path. ⚑ Not the API's `address.url`, which is built from the
 * server's configured web URL — the page that serves `/f/…` is this one, and
 * a manager on a preview or local build would otherwise copy, and print, an
 * address on another host.
 */
export function addressUrl(address) {
	if (!address?.path) return '';
	const origin = siteOrigin();
	return origin ? `${origin}${address.path}` : (address.url || address.path);
}

/** The address as it reads on a row: host and path, no scheme. */
export const addressLabel = address => addressUrl(address).replace(/^https?:\/\//, '');

/**
 * Why keepr paused a form by itself (KPR-514; keepr-api docs/SCHEMA.md § "The
 * form pauses itself"), in words for its managers — or null when a manager
 * paused it, or it is not paused. `pause.by` is null for these: nobody did it.
 *
 * Only the manage side ever says this. The public page answers the one
 * sentence whoever paused it (the API never sends the reason there): a
 * spammer must not learn that their flood is what stopped the form.
 *
 * ⚑ Each sentence names what happened and what to do, never the rule's
 * thresholds: "25 networks" means nothing to a manager, and the numbers are
 * keepr's to tune without the page going stale.
 */
const PAUSE_REASONS = Object.freeze({
	spike: 'keepr paused it because far more submissions arrived at once than this form usually gets. Look over what came in, then resume it.',
	bot: 'keepr paused it because many visitors failed the bot check while some of their submissions still got through. Look over what came in, then resume it.',
	reports: 'keepr paused it because visitors in several places reported it as abuse. Check the form and what came in, then resume it.',
});

export const pauseReasonText = form => (form?.state === 'paused' && PAUSE_REASONS[form?.pause?.reason]) || null;

/**
 * What a form's state badge says (standards: a status always ships with a
 * word). `backpressure` is not a stored state: a live form whose review queue
 * reached its `maxPending` serves "This form is paused" until someone
 * triages, and resumes by itself — the manager needs to know both.
 */
export function formStatus(form) {
	if (!form) return { key: 'unknown', label: 'Unknown', color: 'default' };
	// KPR-360: an invite link's definition a package prepared — nothing to
	// reach until someone creates its link (under Sharing).
	if (form.kind === 'invite-draft') return { key: 'invite-draft', label: 'Not live', color: 'default', hint: 'No link exists yet, so nobody can use it.' };
	switch (form.state) {
		case 'live':
			// KPR-515: a card change stopped it — its address says "This form
			// needs updating by its owner." until the form is edited to fit
			// (keepr-api: `outOfDateSince`, its managers told once).
			if (form.outOfDateSince) return { key: 'out-of-date', label: 'Needs updating', color: 'red', hint: 'A change to its card stopped it. Edit the form to fit the card, and it takes submissions again.' };
			// KPR-482: a form with review rules is held against its queue submitted
			// before the last hour or two only (keepr-api formTallies.waitingCount),
			// so the address never tells a visitor what a rule did. The manager may
			// see more waiting than the limit and the form still open, or clear the
			// newest and see it stay full; the hint says why.
			if (form.backpressure) {
				const ruled = Array.isArray(form.review?.rules) && form.review.rules.length > 0;
				return {
					key: 'full', label: 'Paused — review queue full', color: 'gold',
					hint: ruled
						? 'It takes submissions again by itself once fewer are waiting for review. Because this form has review rules, a submission counts here only once it has waited an hour or two.'
						: 'It takes submissions again by itself once fewer are waiting for review.',
				};
			}
			// Its address moved to another form: live, and out of reach.
			if (!form.address) return { key: 'live', label: 'Live', color: 'green', hint: 'It has no address, so nobody can reach it. Point an address here to put it back.' };
			return { key: 'live', label: 'Live', color: 'green' };
		case 'paused': {
			const why = pauseReasonText(form);
			return why
				? { key: 'paused-by-keepr', label: 'Paused by keepr', color: 'gold', hint: why }
				: { key: 'paused', label: 'Paused', color: 'gold' };
		}
		case 'draft':
			// KPR-360: a draft — a package's prepared form among them — is said
			// to be NOT LIVE in so many words: nobody can reach it.
			return { key: 'draft', label: 'Not live', color: 'default', hint: 'It has no address yet, so nobody can reach it.' };
		case 'retired':
			return { key: 'retired', label: 'Retired', color: 'default' };
		default:
			return { key: form.state || 'unknown', label: form.state || 'Unknown', color: 'default' };
	}
}

export const isRetired = form => form?.state === 'retired';
export const isInviteDraft = form => form?.kind === 'invite-draft';
/** A public form's draft, not retired: what "Make it live…" opens (KPR-360). */
export const isPublicDraft = form => !isInviteDraft(form) && form?.state === 'draft';
/** An invite link's prepared definition, not discarded: what Sharing lists to make a link from (KPR-360). */
export const isPreparedInvite = form => isInviteDraft(form) && !isRetired(form);

/**
 * Does the collection's settings show the Public forms tab? `status` and
 * `data` are hooks/usePublicForms's reading. Where the API has public forms
 * and its switch is on — and, with the server's switch off, where a public
 * draft is here (KPR-360: a package prepared it), so it is shown NOT LIVE
 * with why nothing can make it live yet, rather than hidden.
 */
export const publicFormsTabShown = (status, data) => status === 'on'
	|| (status === 'off' && Array.isArray(data?.forms) && data.forms.some(isPublicDraft));

// "a, b and c".
const inWords = parts => (parts.length < 2 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

/**
 * What a form would expose once it is live, in plain words, one sentence a
 * line (KPR-360: a package's prepared form is made live only after its
 * manager has read this). `labelOf(name)` names an element as its card
 * labels it; `cardName` names the card. For an invite link's draft, its cap
 * and expiry; for a public form, that its address never expires.
 */
export function formExposure(form, { cardName = null, labelOf = name => name } = {}) {
	const d = form?.definition || {};
	const names = list => (Array.isArray(list) ? list.filter(Boolean).map(labelOf) : []);
	const invite = isInviteDraft(form);
	const lines = [];
	const what = cardName ? `a new ${cardName} item` : 'a new item';
	lines.push(invite
		? `Anyone who has the link can send this collection ${what}, without an account.`
		: `Anyone who has the address can send this collection ${what}, without an account. The address is not secret: it is meant for signs, posters and pages.`);
	const asked = names(d.fields);
	if (asked.length) lines.push(`It asks for ${inWords(asked)}.`);
	const privately = names(d.privateFields);
	if (privately.length) lines.push(`It asks for ${inWords(privately)} privately: only the people who manage this collection see ${privately.length === 1 ? 'the answer' : 'those answers'}.`);
	const shown = names(d.shownFields);
	if (shown.length) lines.push(`It shows ${inWords(shown)}, which visitors cannot change.`);
	// What it fills in, WITH the value when it is one to read (the KPR-360
	// review's F1: a prepared form's prefill is stamped on every submission).
	const prefill = d.prefill && typeof d.prefill === 'object' ? d.prefill : {};
	const filled = Object.entries(prefill).map(([name, value]) => (
		['string', 'number', 'boolean'].includes(typeof value) && `${value}` !== ''
			? `${labelOf(name)} with “${value}”`
			: labelOf(name)
	));
	if (filled.length) lines.push(`It fills in ${inWords(filled)} on every submission.`);
	if (d.uploads && typeof d.uploads === 'object') {
		const photosOnly = Array.isArray(d.uploads.types) && d.uploads.types.length === 1 && d.uploads.types[0] === 'photo';
		lines.push(`Visitors can attach ${photosOnly ? 'photos' : 'files'}.`);
	}
	if (invite) {
		const cap = Number.isInteger(form?.invite?.maxSubmissions) ? form.invite.maxSubmissions : null;
		const days = Number.isInteger(form?.invite?.expiresInDays) ? form.invite.expiresInDays : null;
		if (cap && days) lines.push(`The link stops after ${cap === 1 ? '1 submission' : `${cap.toLocaleString('en-US')} submissions`} or ${days === 1 ? '1 day' : `${days} days`}, whichever comes first.`);
	} else {
		lines.push('The address never expires: it keeps working until you pause or retire the form.');
	}
	// Its review rules (KPR-482) may accept a submission with no person —
	// said rather than promising every one waits (the review's F5).
	const ruled = Array.isArray(form?.review?.rules) && form.review.rules.length > 0;
	lines.push(ruled
		? 'Its review rules may accept some submissions without anyone looking; the rest wait for review before they join the list.'
		: 'Each submission waits for review before it joins the list.');
	return lines;
}
export const canPause = form => !isInviteDraft(form) && form?.state === 'live';
export const canResume = form => !isInviteDraft(form) && form?.state === 'paused';

/** Why the server would refuse a form here now (the list's `unavailable`), for a manager. */
export function unavailableText(code) {
	switch (code) {
		case 'public_forms_off':
			return 'Public forms are not switched on for this keepr server yet.';
		case 'bot_check_required':
			return 'This keepr server has no bot check set up, and a public form cannot run without one. Until it does, no form here can be made or resumed.';
		case 'intake_not_enabled':
			return 'Public forms use this collection’s Intake forms switch, which is off. Turn it on under Sharing to make one — and while it is off, every address here answers that there is no form.';
		case 'intake_collection_archived':
			return 'An archived collection cannot take submissions.';
		default:
			return null;
	}
}

/**
 * A refusal on the manage side, in words a manager can act on. The
 * definition's refusals are the intake link's, code for code (the API judges
 * both with one module), so they fall through to describeIntakeError —
 * except where its words name the invite link.
 */
export function describePublicFormError(error, elementsByName = {}, fallback = 'The public form could not be saved.') {
	const data = error?.response?.data || {};
	switch (data.code) {
		case 'public_forms_off':
		case 'bot_check_required':
			return unavailableText(data.code);
		case 'intake_not_enabled':
			return 'Intake forms are switched off for this collection. Turn them on under Sharing, then try again.';
		case 'public_forms_unavailable':
			return 'Public forms are not ready on this keepr server. Try again later.';
		case 'acknowledge_required':
			return 'Tick the box that says anyone with the address can send a submission.';
		case 'form_name_required':
			return 'Give the form a name.';
		case 'form_name_too_long':
			return `A form’s name is at most ${MAX_FORM_NAME} characters.`;
		case 'form_limits_invalid':
			return data.message || 'The limits are out of range.';
		case 'slug_taken':
			return data.message || 'That address is taken. Choose another.';
		case 'slug_reserved':
		case 'slug_invalid':
			return data.message || 'That address cannot be used. Choose another.';
		case 'collection_slug_set':
			return `This collection’s address is already ${data.collectionSlug ? `“${data.collectionSlug}”` : 'chosen'}, and it does not change.`;
		case 'collection_slug_required':
			return 'Choose an address for the collection first.';
		case 'too_many_addresses':
			return data.message || 'This collection has all the addresses it can have. Move an existing one instead.';
		case 'too_many_forms':
			return data.message || 'This collection has all the public forms it can have.';
		case 'address_conflict':
			return 'Someone else changed this form’s address at the same moment. Try again.';
		case 'form_retired':
			return 'This form has been retired, so it can no longer be changed.';
		case 'form_not_live':
			return 'Only a live form can be paused.';
		case 'form_draft':
			return 'A draft goes live by giving it an address.';
		case 'form_invite_draft':
			return 'This is a prepared invite link, not a public form.';
		case 'card_not_member':
			return 'This form’s card is no longer in the collection.';
		case 'card_id_required':
			return 'Choose the card the form fills in.';
		case 'email_unverified':
		case 'session_required':
			return data.message || fallback;
		case 'link_revoked':
		case 'link_not_editable':
			return data.message || fallback;
		default:
			break;
	}
	const said = describeIntakeError(error, elementsByName);
	return said === 'The intake link could not be saved.' ? (data.message || fallback) : said;
}

// ------------------------------------------------------------------ the visitor

export const PAUSED_TEXT = 'This form is paused.';
export const NO_FORM_TITLE = 'There is no form at this address';
export const LIMIT_TITLE = 'This form has taken all the submissions it can for now';
export const UNREACHABLE_TITLE = 'This form could not be reached';

/** Is this the address's 429 `form_limit_reached`? */
export const isLimitReached = error => error?.response?.status === 429 && error?.response?.data?.code === 'form_limit_reached';
/** Is this the address's 409 `form_paused`? */
export const isFormPaused = error => error?.response?.status === 409 && error?.response?.data?.code === 'form_paused';

/** "Try again in 40 minutes." from a 429's Retry-After, or the plain sentence. */
export function limitRetryText(error) {
	const wait = retryAfterSeconds(error);
	return wait ? `Try again in ${formatWait(wait)}.` : 'Try again later.';
}

/**
 * "Report this form" (KPR-514): what a visitor reads for each answer of
 * `POST /api/forms/{c}/{f}/report`. Every answer is said in words; none of
 * them says whether the report paused the form (the API answers 200 either
 * way, and so does this).
 */
export function describeFormReportError(error) {
	const status = error?.response?.status;
	const code = error?.response?.data?.code;
	if (!status) return 'The report could not reach keepr. Check the connection and try again.';
	if (status === 404) return 'There is no form at this address any more, so there is nothing left to report.';
	if (status === 409 && code === 'form_paused') return 'This form has been paused, so there is nothing to report right now.';
	if (status === 429) return 'Too many reports came from here today. Try again tomorrow.';
	if (status === 503 || code === 'challenge_unavailable') return 'keepr could not run its bot check just now. Try again in a few minutes.';
	switch (code) {
		case 'challenge_failed':
			return 'The bot check did not pass. Wait for it to finish, then send the report again.';
		case 'invalid_reason':
			return 'Choose a reason from the list.';
		case 'text_too_long':
			return 'Say it in 500 characters or fewer.';
		default:
			return 'The report could not be sent. Try again in a few minutes.';
	}
}
